import React, { useMemo, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, message, Modal, ModalProps, TableColumnsType, Tooltip, Table } from 'antd';
import Empty from 'components/Empty';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { useTranslate } from '@tolgee/react';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import StatusSwitch from 'components/StatusSwitch';
import StatusButton from 'components/StatusButton';
import { updateAssociationEnable, UpdateAssociationEnableBody } from 'services/api/phrase';
import { platformLabelMap } from 'services/config/platform';
import { usePhraseContext } from '../PhraseContext';
import { useGetRegionLabel } from 'hooks/business/useRegion';

const EnableTitle = styled.div`
  > span {
    margin-left: 8px;
    cursor: pointer;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Content = styled.section`
  max-height: 60vh;
  margin: -24px;
  padding: 24px;
  overflow: auto;
`;

type RV = Util.ElementType<
  Util.ElementType<Phrase.Statistic['channelPhraseInfo']>['regionPhraseInfo']
>;

type CheckedRegionMap = Partial<Record<General.UpperCaseRegion, boolean>>;

export interface AssociationModalProps extends ModalProps {}

function AssociationModal(props: AssociationModalProps) {
  const { onCancel } = props;

  const { platform, statistic, fetchStatistic } = usePhraseContext();

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const regionList = useMemo(() => {
    if (!statistic) {
      return [];
    }

    const { channelPhraseInfo } = statistic;

    const platformPhrase = channelPhraseInfo.find(
      ({ channel }) => channel === toUpperCase(platform),
    );

    const { regionPhraseInfo = [] } = platformPhrase || {};

    return regionPhraseInfo;
  }, [statistic, platform]);

  const updateEnable = useCallback(
    (body: UpdateAssociationEnableBody) => {
      return updateAssociationEnable(body)
        .then(() => {
          return fetchStatistic();
        })
        .then(() => {
          message.success(t('operation.saveSucceed'));
        })
        .catch(() => {
          message.error(t('operation.saveFailed'));
        });
    },
    [fetchStatistic, t],
  );

  const batchUpdateEnable = useCallback(
    (body: UpdateAssociationEnableBody, e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      return updateAssociationEnable(body)
        .then(() => {
          return fetchStatistic();
        })
        .then(() => {
          message.success(t('operation.saveSucceed'));

          if (onCancel) {
            onCancel(e);
          }
        })
        .catch(() => {
          message.error(t('operation.saveFailed'));
        });
    },
    [fetchStatistic, onCancel, t],
  );

  const [checkedRegionMap, setCheckedRegionMap] = useState<CheckedRegionMap>({});

  const columns: TableColumnsType<RV> = useMemo(() => {
    const allChecked =
      regionList.length > 0 && regionList.every(({ region }) => checkedRegionMap[region]);

    const allIndeterminate =
      !allChecked && regionList.some(({ region }) => checkedRegionMap[region]);

    return [
      {
        key: 'region',
        dataIndex: 'region',
        width: 40,
        title: (
          <Checkbox
            checked={allChecked}
            indeterminate={allIndeterminate}
            onChange={(e) => {
              const {
                target: { checked },
              } = e;

              const newMap = regionList.reduce<CheckedRegionMap>((res, cur) => {
                const { region } = cur;

                res[region] = checked;

                return res;
              }, {});

              setCheckedRegionMap(newMap);
            }}
          />
        ),
        render: (region: RV['region']) => {
          return (
            <Checkbox
              checked={checkedRegionMap[region] || false}
              onChange={(e) => {
                const {
                  target: { checked },
                } = e;

                setCheckedRegionMap((map) => {
                  return {
                    ...map,
                    [region]: checked,
                  };
                });
              }}
            />
          );
        },
      },
      ...(platform === 'FACEBOOK'
        ? []
        : [
            {
              key: 'region',
              dataIndex: 'region',
              title: t('phrase.站点'),
              render: (region: RV['region']) => {
                return getRegionLabel(region);
              },
            },
          ]),
      {
        key: 'associationEnable',
        dataIndex: 'associationEnable',
        title: (
          <EnableTitle>
            {t('phrase.快捷回复联想')}
            <Tooltip title={t('phrase.开启后，在接待回复时输入的内容，会自动联想已开启的快捷回复')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </EnableTitle>
        ),
        render: (enable: RV['associationEnable'], record: RV) => {
          const { region } = record;

          return (
            <StatusSwitch
              checked={enable}
              onChange={(enable) => {
                return updateEnable({
                  channel: toUpperCase(platform),
                  region: [region],
                  associationEnable: enable,
                });
              }}
              checkedChildren={t('phrase.已开启')}
              unCheckedChildren={t('phrase.未启用')}
            />
          );
        },
      },
    ];
  }, [checkedRegionMap, regionList, platform, updateEnable, t, getRegionLabel]);

  const content = useMemo(() => {
    if (!statistic) {
      return <Empty />;
    }

    return <Table rowKey="region" columns={columns} dataSource={regionList} pagination={false} />;
  }, [statistic, columns, regionList]);

  const footer = useMemo(() => {
    const checkedRegionList = Object.keys(checkedRegionMap).filter((region) => {
      const { [region]: checked } = checkedRegionMap;

      return checked;
    });

    const checkedCount = checkedRegionList.length;

    if (checkedCount === 0) {
      return null;
    }

    return (
      <Footer>
        <div>
          <span>{t('phrase.已选中x个站点', { x: checkedCount })}</span>
          <Button type="link" onClick={() => setCheckedRegionMap({})}>
            {t('phrase.取消选择')}
          </Button>
        </div>
        <div>
          <StatusButton
            onClick={(e) => {
              return batchUpdateEnable(
                {
                  channel: toUpperCase(platform),
                  region: checkedRegionList,
                  associationEnable: false,
                },
                e,
              );
            }}
          >
            {t('phrase.批量关闭')}
          </StatusButton>
          <StatusButton
            type="primary"
            onClick={(e) => {
              return batchUpdateEnable(
                {
                  channel: toUpperCase(platform),
                  region: checkedRegionList,
                  associationEnable: true,
                },
                e,
              );
            }}
          >
            {t('phrase.批量开启')}
          </StatusButton>
        </div>
      </Footer>
    );
  }, [checkedRegionMap, t, batchUpdateEnable, platform]);

  return (
    <Modal
      title={`${platformLabelMap[toLowerCase(platform)]} ${t('phrase.输入联想设置')}`}
      footer={footer}
      {...props}
    >
      <Content>{content}</Content>
    </Modal>
  );
}

export default AssociationModal;
